import React, { useMemo } from "react";
import { Box, Button, MenuItem, Paper, TextField } from "@mui/material";
import { Autorenew as AutorenewIcon } from "@mui/icons-material";
import {
  ARCADE_ACCENT,
  customCategoriesForSport,
} from "../../../utils/triviaArcade";
import {
  MIXED_FORMAT,
  PACK_COUNTS,
  PACK_DIFFICULTIES,
  PACK_FORMATS,
  resolvePackMode,
} from "./triviaEnginePack";

const FIELD_SX = { flex: "1 1 160px", minWidth: 150 };

const TriviaPackControls = ({
  sport,
  options,
  onOptionsChange,
  onRegenerate,
  isBusy,
  disabled,
}) => {
  const categories = useMemo(() => customCategoriesForSport(sport), [sport]);
  const update = (field, value) => onOptionsChange({ ...options, [field]: value });
  const mode = resolvePackMode(options);

  return (
    <Paper
      component="section"
      aria-label="Pack controls"
      variant="outlined"
      sx={{ p: 2, bgcolor: "background.paper", borderColor: "divider" }}
    >
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "flex-start",
          gap: 2,
        }}
      >
        <TextField
          select
          size="small"
          label="Category"
          value={options.category}
          onChange={(event) => update("category", event.target.value)}
          helperText="Career Stats spans every slot"
          sx={FIELD_SX}
        >
          {categories.map((category) => (
            <MenuItem key={category.value} value={category.value}>
              {category.label}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          size="small"
          label="Format"
          value={options.format}
          onChange={(event) => update("format", event.target.value)}
          helperText={
            options.format === MIXED_FORMAT
              ? "Uses the preset format mix"
              : "Every slot uses this format"
          }
          sx={FIELD_SX}
        >
          {PACK_FORMATS.map((format) => (
            <MenuItem key={format.value} value={format.value}>
              {format.label}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          size="small"
          label="Difficulty"
          value={options.difficulty}
          onChange={(event) => update("difficulty", event.target.value)}
          helperText="Picks the fame and gap windows"
          sx={FIELD_SX}
        >
          {PACK_DIFFICULTIES.map((difficulty) => (
            <MenuItem key={difficulty.value} value={difficulty.value}>
              {difficulty.label}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          size="small"
          label="Questions"
          value={options.count}
          onChange={(event) => update("count", Number(event.target.value))}
          helperText={`Engine mode: ${mode}`}
          sx={FIELD_SX}
        >
          {PACK_COUNTS.map((count) => (
            <MenuItem key={count} value={count}>
              {count}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          size="small"
          label="Seed"
          value={options.seed}
          onChange={(event) => update("seed", event.target.value)}
          placeholder="Optional"
          helperText="Repeats the same pack"
          sx={FIELD_SX}
        />

        <Button
          variant="contained"
          startIcon={<AutorenewIcon />}
          onClick={onRegenerate}
          disabled={disabled || isBusy}
          sx={{
            height: 40,
            px: 3,
            fontWeight: 800,
            ml: { md: "auto" },
            bgcolor: ARCADE_ACCENT,
            color: "#fff",
            "&:hover": { bgcolor: "#db2777" },
          }}
        >
          Regenerate
        </Button>
      </Box>
    </Paper>
  );
};

export default TriviaPackControls;
